import { InputAdornment, Stack, useTheme } from '@mui/material';
import { ConversationParticipantEntity } from '@provision/conversation-types';
import { useEffect, useMemo } from 'react';
import {
  DynamicIcon,
  FormAutocomplete,
  FormAutocompleteProps,
  FormField,
  FormLabel,
} from 'src/components';
import { componentsFontSizes } from 'src/constants/layout';
import {
  Conversation,
  ConversationStatusOption,
  conversationTypeOptions,
  DEFAULT_STATUS,
  getPreparedConversationParticipantOptionGroup,
  PreparedConversationParticipantOption,
  useConversationStatusOptions,
  useConversationUrgencyOptions,
} from 'src/graphql';
import { useConversationUpdate } from 'src/graphql/entities/conversation/use-conversation-update';
import {
  ConversationAssigneeOption,
  useConversationAssigneeOptions,
} from 'src/graphql/entities/platform-user';
import { appendDisabledUserTextToName } from 'src/graphql/entities/platform-user/utils';
import { useSelectedConversationRequesterStore } from 'src/store/selected-conversation-requester';
import { ConversationParticipantRaw } from 'src/types';
import { ConversationAssigneeToggle } from './conversation-assignee-toggle';
import { ConversationRequesterToggle } from './conversation-requester-toggle';
import { ConversationStatusToggle } from './conversation-status-toggle';
import { ConversationTypeToggle } from './conversation-type-toggle';
import { ConversationUrgencyToggle } from './conversation-urgency-toggle';

export interface ConversationDetailsFormValues {
  conversationStatus: ConversationStatusOption;
  assignee: ConversationAssigneeOption | null;
  group: { id: string; label: string };
  type: { id: string; label: string };
  urgency: { id: string; label: string };
  requester: { id: string; label: string; participantEntity: ConversationParticipantEntity };
}

interface GetDefaultConversationParticipantOptionInput {
  requester: ConversationParticipantRaw;
  participantEntity?: ConversationParticipantEntity;
  participantIdentity?: string;
  conversationParticipantId?: string;
  fullName?: string;
  phoneNumber?: string;
  email?: string;
}

export function useConversationPropertiesForm(
  conversation: Conversation,
  shouldDisableFormFields?: boolean,
) {
  const theme = useTheme();

  const { updateConversation } = useConversationUpdate();

  const urgencyOptions = useConversationUrgencyOptions(conversation.partner?.id);

  const { selectedAssignee, assigneeOptions } = useConversationAssigneeOptions(conversation);
  const { selectedConversationRequester, setPartnerSelectedConversationRequester } =
    useSelectedConversationRequesterStore((state) => state);

  const conversationParticipantOption = useMemo(() => {
    return (
      selectedConversationRequester ||
      getDefaultConversationParticipantOption({
        requester: conversation.requester,
        participantIdentity: conversation.requesterData?.participantIdentity,
        participantEntity: conversation.requesterData?.participantEntity,
        conversationParticipantId: conversation.requesterData?.conversationParticipantId,
        fullName: conversation.requesterFullName,
        email: conversation.requesterData?.email,
        phoneNumber: conversation.requesterData?.phoneNumber,
      })
    );
  }, [
    selectedConversationRequester,
    conversation.requester,
    conversation.requesterData?.participantIdentity,
    conversation.requesterData?.participantEntity,
    conversation.requesterData?.conversationParticipantId,
    conversation.requesterData?.email,
    conversation.requesterData?.phoneNumber,
    conversation.requesterFullName,
  ]);

  useEffect(
    () => setPartnerSelectedConversationRequester(null),
    [setPartnerSelectedConversationRequester, conversation.requesterId],
  );

  const statusOptions = useConversationStatusOptions();

  const conversationPropertiesFormFields = useMemo<FormField[]>(() => {
    const commonProps = {
      autocompleteProps: {
        forcePopupIcon: false,
        disableClearable: true,
        autoHighlight: true,
        disabled: shouldDisableFormFields,
        sx: {
          padding: theme.spacing(0.5, 1),
          borderRadius: theme.shape.borderRadius / 8,
          cursor: 'pointer',
          '&:hover': {
            backgroundColor: theme.palette.custom.OV.sys.surface,
          },
          '& .MuiInput-root': {
            cursor: 'pointer',
          },
          '& .MuiInput-input': {
            cursor: 'pointer',
          },
          '&.Mui-focused': {
            backgroundColor: theme.palette.custom.OV.sys.surfaceContainerHigh,
          },
          '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
            cursor: 'pointer',
            padding: 0,
            border: '0',
            fieldset: {
              display: 'none',
            },
            '.MuiAutocomplete-input': {
              cursor: 'pointer',
              fontSize: '0.9rem',
              padding: 0,
              marginLeft: '0.6rem',
            },
          },
        },
        ListboxProps: {
          sx:
            theme.palette.mode === 'dark'
              ? {
                  background: theme.palette.custom.OV.sys.surfaceContainerHigh,
                  border: `1px solid ${theme.palette.custom.OV.sys.outlineVariant}`,
                  borderRadius: theme.shape.borderRadius / 8,
                }
              : {},
        },
      },
      textFieldProps: {
        size: 'small',
      },
      inputProps: {
        sx: {
          ...theme.typography.body2,
          fontSize: theme.utils.important(componentsFontSizes.input),
        },
      },
      renderField,
    };

    const conversationStatus =
      statusOptions.find((item) => item.id === conversation.conversationStatusId) ||
      DEFAULT_STATUS;
    return [
      {
        name: 'conversationStatus',
        label: 'Status',
        defaultValue: conversationStatus,
        renderField(field: FormAutocompleteProps) {
          return (
            <Stack direction="row" alignItems="center" px={2} key={field.label}>
              <FormLabel label={field.label} />
              <Stack direction="row" width="100%" gap={1}>
                <ConversationStatusToggle
                  name={field.name}
                  disabled={commonProps.autocompleteProps.disabled}
                />
              </Stack>
            </Stack>
          );
        },
      },
      {
        name: 'assignee',
        label: 'Assignee',
        defaultValue: selectedAssignee ?? null,
        renderField(field: FormAutocompleteProps) {
          return (
            <Stack direction="row" alignItems="center" px={2} key={field.label}>
              <FormLabel label={field.label} />
              <Stack direction="row" width="100%" gap={1}>
                <ConversationAssigneeToggle
                  name={field.name}
                  options={assigneeOptions}
                  disabled={commonProps.autocompleteProps.disabled}
                />
              </Stack>
            </Stack>
          );
        },
      },
      {
        name: 'requester',
        label: 'Requester',
        defaultValue: conversationParticipantOption,
        renderField(field: FormAutocompleteProps) {
          return (
            <Stack direction="row" alignItems="center" px={2} key={field.label}>
              <FormLabel label={field.label} />
              <Stack direction="row" width="100%" gap={1}>
                <ConversationRequesterToggle
                  name={field.name}
                  partnerId={conversation.partnerId}
                  conversationId={conversation.conversationId}
                  disabled={commonProps.autocompleteProps.disabled}
                />
              </Stack>
            </Stack>
          );
        },
      },
      {
        name: 'type',
        label: 'Type',
        defaultValue:
          conversationTypeOptions.find((item) => item.id === conversation.type) || null,
        renderField(field: FormAutocompleteProps) {
          return (
            <Stack direction="row" alignItems="center" px={2} key={field.label}>
              <FormLabel label={field.label} />
              <Stack direction="row" width="100%" gap={1}>
                <ConversationTypeToggle
                  name={field.name}
                  disabled={commonProps.autocompleteProps.disabled}
                />
              </Stack>
            </Stack>
          );
        },
      },
      {
        name: 'urgency',
        label: 'Urgency',
        defaultValue: urgencyOptions.find((item) => item.id === conversation.urgency) || null,
        renderField(field: FormAutocompleteProps) {
          return (
            <Stack direction="row" alignItems="center" px={2} key={field.label}>
              <FormLabel label={field.label} />
              <Stack direction="row" width="100%" gap={1}>
                <ConversationUrgencyToggle
                  name={field.name}
                  partnerId={conversation.partner?.id}
                  disabled={commonProps.autocompleteProps.disabled}
                />
              </Stack>
            </Stack>
          );
        },
      },
    ] as (FormAutocompleteProps & { iconColor?: string })[];
    // Override to hook for palette mode updates.
  }, [
    shouldDisableFormFields,
    theme,
    statusOptions,
    selectedAssignee,
    assigneeOptions,
    conversation.urgency,
    conversation.conversationStatusId,
    conversation.conversationId,
    conversation.partnerId,
    conversation.partner?.id,
    conversation.type,
    urgencyOptions,
    conversationParticipantOption,
  ]);

  return { conversationPropertiesFormFields, updateConversation };
}

function renderField(field: FormAutocompleteProps & { iconColor?: string }) {
  const { type, defaultValue, label, icon, iconColor, options, renderField, ...rest } = field;
  return (
    <Stack direction="row" alignItems="center" px={2} key={label}>
      <FormLabel label={label} />
      <Stack direction="row" width="100%" gap={1}>
        <FormAutocomplete
          type={type}
          defaultValue={defaultValue}
          options={options}
          {...rest}
          inputProps={{
            ...rest.inputProps,
            startAdornment: (
              <InputAdornment position="start">
                {typeof icon === 'string' ? (
                  <DynamicIcon
                    sx={{ color: iconColor || ((theme) => theme.palette.text.secondary) }}
                    name={icon}
                  />
                ) : (
                  icon
                )}
              </InputAdornment>
            ),
          }}
        />
      </Stack>
    </Stack>
  );
}

function getDefaultConversationParticipantOption(
  input: GetDefaultConversationParticipantOptionInput,
): PreparedConversationParticipantOption | null {
  if (!input.participantIdentity) return null;

  let label = input.fullName?.trim() || '';

  if (
    input.participantEntity === ConversationParticipantEntity.PlatformUser &&
    !input.requester?.platformUser?.isActive
  ) {
    label = appendDisabledUserTextToName(label);
  }

  return {
    id: input.participantIdentity ?? '',
    value: input.participantIdentity ?? '',
    label,
    group: getPreparedConversationParticipantOptionGroup(input.participantEntity!),
    participantEntity: input.participantEntity || ConversationParticipantEntity.Client,
    conversationParticipantId: input.conversationParticipantId ?? '',
    phoneNumber: input.phoneNumber ?? '',
    email: input.email ?? '',
    entityPartnerId: input.requester?.platformUser?.partnerId,
    avatarFileKey: input.requester?.platformUser?.avatarFileKey ?? null,
  };
}
